import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import REPORT_HEADER from 'assets/images/report-header.webp';
import {
    IFeature,
    IOfferEventType,
    OffersSettingsDocument,
} from 'graphql/types';
import { useFeature } from 'common/hooks/useFeature';
import { BackgroundImg } from 'common/components/Img';
import { PageTitle } from 'common/components/PageTitle';
import { Tab, Tabs } from 'common/components/Tabs';
import { RouteLink } from 'route/components/RouteLink';
import { PortfolioReportTable } from 'report/components/PortfolioReportTable';
import { CourseReportTable } from 'report/components/CourseReportTable';
import { TrainingReportTable } from 'report/components/TrainingReportTable';
import { Loader } from 'common/components/Loader';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { FourOFourPage } from 'hydra/pages/FourOFourPage';
import { UpgradeContent as PortfolioUpgradeContent } from 'user/components/UpgradeContent';
import { useAppPermissions } from 'user/hooks/usePermissions';
import { OfferReportTable } from 'report/components/OfferReportTable';
import { UpgradeContent as OffersUpgradeContent } from 'offer/components/UpgradeContent';
import { getUrl } from 'route/utils/getUrl';
import { TRouteName } from 'route/types';

import { BasePage } from './BasePage';

export const PortfolioReportPage = () => {
    const [translate] = useTranslation();

    const {
        canReadPortfolio = false,
        canReadTrainings = false,
        canReadOffers = false,
        canReadModules = false,
    } = useAppPermissions('portfolioReport', 'general');

    const isPortfolioReportPage = !!useRouteMatch('PORTFOLIO_REPORT');
    const isTrainingReportPage = !!useRouteMatch('TRAINING_REPORT');
    const isOfferReportPage = !!useRouteMatch('OFFER_REPORT');
    const isMeetingReportPage = !!useRouteMatch('MEETING_REPORT');
    const isCourseReportPage = !!useRouteMatch('COURSE_REPORT');

    const {
        userCanSeeUpgrade: userCanSeePortfolioUpgrade,
        loading: loadingCanUsePortfolio,
    } = useFeature(IFeature.Portfolio);
    const {
        userCanSeeUpgrade: userCanSeeOffersUpgrade,
        canUse: canUseOffers,
        loading: loadingCanUseOffers,
    } = useFeature(IFeature.Offer);

    const { data: offersSettingsData, loading: loadingOffersSettings } =
        useQuery(OffersSettingsDocument, {});
    const { publishElearning = false, publishMeetingWebinar = false } =
        offersSettingsData?.offersSettings || {};

    const isTrainingTabAvailable = canReadTrainings;
    const isOfferTabAvailable =
        canReadOffers && canUseOffers && publishElearning;
    const isMeetingTabAvailable =
        canReadOffers && canUseOffers && publishMeetingWebinar;
    const isPortfolioTabAvailable = canReadPortfolio;
    const isCourseTabAvailable = canReadModules;

    const canReadPortfolioReport =
        (isPortfolioReportPage && isPortfolioTabAvailable) ||
        (isTrainingReportPage && isTrainingTabAvailable) ||
        (isOfferReportPage && isOfferTabAvailable) ||
        (isMeetingReportPage && isMeetingTabAvailable) ||
        (isCourseReportPage && isCourseTabAvailable);

    let activeTab = 1;

    if (isTrainingReportPage) activeTab = 1;
    if (isOfferReportPage) activeTab = 2;
    if (isMeetingReportPage) activeTab = 3;
    if (isPortfolioReportPage) activeTab = 4;
    if (isCourseReportPage) activeTab = 5;

    const isLoading =
        loadingCanUsePortfolio || loadingCanUseOffers || loadingOffersSettings;

    const showOffersUpgradeContent =
        !isLoading &&
        userCanSeeOffersUpgrade &&
        ((!canReadOffers && (isOfferReportPage || isMeetingReportPage)) ||
            (!canUseOffers && (isOfferReportPage || isMeetingReportPage)));

    const showPortfolioUpgradeContent =
        !isLoading &&
        !canReadPortfolioReport &&
        userCanSeePortfolioUpgrade &&
        isPortfolioReportPage;

    const fallbackRoute: TRouteName | null = isTrainingTabAvailable
        ? 'TRAINING_REPORT'
        : isOfferTabAvailable
          ? 'OFFER_REPORT'
          : isMeetingTabAvailable
            ? 'MEETING_REPORT'
            : isPortfolioTabAvailable
              ? 'PORTFOLIO_REPORT'
              : isCourseTabAvailable
                ? 'COURSE_REPORT'
                : null;

    const shouldRedirect =
        !isLoading &&
        !canReadPortfolioReport &&
        !showOffersUpgradeContent &&
        !showPortfolioUpgradeContent &&
        !!fallbackRoute;

    const navigate = useNavigate();

    useEffect(() => {
        if (shouldRedirect && fallbackRoute) {
            navigate(getUrl(fallbackRoute), { replace: true });
        }
    }, [shouldRedirect, fallbackRoute, navigate]);

    if (
        !isLoading &&
        !canReadPortfolioReport &&
        !showOffersUpgradeContent &&
        !showPortfolioUpgradeContent &&
        !fallbackRoute
    ) {
        return <FourOFourPage />;
    }

    const header = (
        <BackgroundImg
            src={REPORT_HEADER}
            sx={{
                height: '10vw',
                maxHeight: '150px',
                backgroundSize: 'contain',
                backgroundPosition: 'right top',
                display: { xs: 'none', sm: 'block' },
            }}
        />
    );

    return (
        <BasePage contentContainerMaxWidth="lg" header={header}>
            {isLoading || shouldRedirect ? (
                <Loader />
            ) : (
                <>
                    {canReadPortfolioReport && (
                        <>
                            <PageTitle>
                                {translate('reportPage.browserTitle')}
                            </PageTitle>

                            <Box sx={{ pt: { xs: 4, sm: 2 } }}>
                                <Typography variant="h1">
                                    {translate('reportPage.pageTitle')}
                                </Typography>
                            </Box>
                            <Tabs sx={{ mb: 4, mt: 2 }} value={activeTab}>
                                {isTrainingTabAvailable && (
                                    <Tab
                                        component={RouteLink}
                                        label={translate(
                                            'reportPage.tabTraining'
                                        )}
                                        to="TRAINING_REPORT"
                                        value={1}
                                    />
                                )}

                                {canReadOffers &&
                                    canUseOffers &&
                                    publishElearning && (
                                        <Tab
                                            component={RouteLink}
                                            label={translate(
                                                'reportPage.tabOffers'
                                            )}
                                            to="OFFER_REPORT"
                                            value={2}
                                        />
                                    )}

                                {canReadOffers &&
                                    canUseOffers &&
                                    publishMeetingWebinar && (
                                        <Tab
                                            component={RouteLink}
                                            label={translate(
                                                'reportPage.tabMeeting'
                                            )}
                                            to="MEETING_REPORT"
                                            value={3}
                                        />
                                    )}

                                {canReadPortfolio && (
                                    <Tab
                                        component={RouteLink}
                                        label={translate(
                                            'reportPage.tabPortfolio'
                                        )}
                                        to="PORTFOLIO_REPORT"
                                        value={4}
                                    />
                                )}

                                {isCourseTabAvailable && (
                                    <Tab
                                        component={RouteLink}
                                        label={translate(
                                            'reportPage.tabCourses'
                                        )}
                                        to="COURSE_REPORT"
                                        value={5}
                                    />
                                )}
                            </Tabs>

                            {isPortfolioReportPage && <PortfolioReportTable />}
                            {isTrainingReportPage && <TrainingReportTable />}
                            {isOfferReportPage &&
                                canUseOffers &&
                                publishElearning && (
                                    <OfferReportTable
                                        offerEventType={[
                                            IOfferEventType.Training,
                                            IOfferEventType.Course,
                                            IOfferEventType.Elearning,
                                        ]}
                                    />
                                )}
                            {isMeetingReportPage &&
                                canUseOffers &&
                                publishMeetingWebinar && (
                                    <OfferReportTable
                                        offerEventType={[
                                            IOfferEventType.Meeting,
                                            IOfferEventType.Webinar,
                                        ]}
                                    />
                                )}
                            {isCourseReportPage && <CourseReportTable />}
                        </>
                    )}

                    {showPortfolioUpgradeContent && <PortfolioUpgradeContent />}

                    {showOffersUpgradeContent && <OffersUpgradeContent />}
                </>
            )}
        </BasePage>
    );
};
